import * as XLSX from "xlsx";

import { SHEET_DISCOVERY_MAX_BYTES } from "@/constants/uploadLimits";

/**
 * Ceiling for client-side workbook parsing, configured per environment via
 * VITE_SHEET_DISCOVERY_MAX_MIB. Real-world reference workbooks are under 1 MB;
 * the ceiling guards the main thread against pathological inputs.
 *
 * This is NOT an upload limit (AB#6463). A workbook above it is still uploaded
 * — it just skips the sheet picker and lets the server extract sheets. The real
 * per-file-type caps live in @/constants/uploadLimits and are enforced by the
 * server with 413 / VAL-000003.
 */
export { SHEET_DISCOVERY_MAX_BYTES };

/**
 * Discriminated result for sheet-name discovery.
 *
 * - `ok`: parsed successfully; `sheetNames` is non-empty.
 * - `not_excel`: caller passed a non-workbook file; the dropzone should
 *   keep the file (CSV/PDF/etc. don't need a sheet picker).
 * - `too_large`: workbook exceeds {@link SHEET_DISCOVERY_MAX_BYTES}, so its
 *   sheets were not enumerated. The dropzone KEEPS the file and uploads it
 *   without a sheet picker (AB#6463) — this is not a rejection.
 * - `password_protected`: SheetJS threw an encryption-flavored error; the
 *   dropzone rejects it so the user re-uploads an unlocked copy.
 * - `unparseable`: SheetJS threw for any other reason (corrupt bytes,
 *   truncated archive, etc.); the dropzone rejects it.
 */
export type SheetDiscoveryResult =
  | { status: "ok"; sheetNames: string[] }
  | { status: "not_excel" }
  | { status: "too_large" }
  | { status: "password_protected" }
  | { status: "unparseable" };

/**
 * Does the file's name suggest a workbook we can parse for sheet names?
 * Uses the filename rather than MIME because FilePond can surface generic
 * application/octet-stream for some browser/OS combos.
 */
export const isExcelFile = (file: File): boolean => {
  const lower = file.name.toLowerCase();
  return lower.endsWith(".xlsx") || lower.endsWith(".xls");
};

// SheetJS reports password-protected workbooks via thrown errors whose
// message contains "password" or "encrypted" (e.g. "File is password-
// protected (Standard)"). Anything else from the catch block is treated
// as generic unparseable bytes.
const PASSWORD_ERROR = /password|encrypt/i;

const isPasswordError = (err: unknown): boolean => {
  if (err instanceof Error) return PASSWORD_ERROR.test(err.message);
  if (typeof err === "string") return PASSWORD_ERROR.test(err);
  return false;
};

/**
 * Parses a workbook client-side and returns a discriminated result the
 * dropzone can act on. Never throws — the result is always a
 * {@link SheetDiscoveryResult}.
 */
export const discoverSheetNames = async (file: File): Promise<SheetDiscoveryResult> => {
  if (!isExcelFile(file)) {
    return { status: "not_excel" };
  }
  if (file.size > SHEET_DISCOVERY_MAX_BYTES) {
    return { status: "too_large" };
  }
  try {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array", bookSheets: true });
    if (wb.SheetNames.length === 0) {
      return { status: "unparseable" };
    }
    return { status: "ok", sheetNames: wb.SheetNames };
  } catch (err) {
    return { status: isPasswordError(err) ? "password_protected" : "unparseable" };
  }
};
